import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Dialog } from "../../components/ui/dialog";
import { extractToc, parseVideoEmbed, renderHelpMarkdown } from "./markdown";

export function HelpMarkdown({ content }: { content: string }) {
  const { t } = useTranslation();
  const html = useMemo(() => renderHelpMarkdown(content), [content]);
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);
  const [zoomAlt, setZoomAlt] = useState("");

  return (
    <>
      <div
        className="help-prose"
        dangerouslySetInnerHTML={{ __html: html }}
        onClick={(event) => {
          const target = event.target as HTMLElement;
          if (target.tagName === "IMG") {
            const image = target as HTMLImageElement;
            setZoomSrc(image.src);
            setZoomAlt(image.alt || t("help.screenshot", "Capture d'écran"));
          }
        }}
      />
      <Dialog
        open={Boolean(zoomSrc)}
        onOpenChange={(open) => {
          if (!open) setZoomSrc(null);
        }}
        title={zoomAlt}
        maxWidth="1100px"
      >
        {zoomSrc ? (
          <img src={zoomSrc} alt={zoomAlt} className="max-h-[75vh] w-full rounded-lg object-contain" />
        ) : null}
      </Dialog>
    </>
  );
}

export function HelpVideo({ url }: { url: string }) {
  const { t } = useTranslation();
  const embed = parseVideoEmbed(url);
  if (!embed) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium">
        {t("help.watchVideo", "Voir la vidéo")}
      </a>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-sidebar-border bg-primary">
      <iframe
        src={embed.src}
        title={t("help.watchVideo", "Voir la vidéo")}
        className="aspect-video w-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

export function HelpToc({ markdown }: { markdown: string }) {
  const { t } = useTranslation();
  const items = useMemo(() => extractToc(markdown), [markdown]);
  if (items.length < 2) return null;
  const list = (
    <ol className="space-y-2 text-sm">
      {items.map((item) => (
        <li key={item.id} className={item.level === 3 ? "pl-3" : undefined}>
          <a href={`#${item.id}`} className="text-muted-foreground hover:text-foreground">
            {item.text}
          </a>
        </li>
      ))}
    </ol>
  );
  return (
    <>
      <details className="mb-8 rounded-lg border border-sidebar-border bg-sidebar/70 p-4 lg:hidden">
        <summary className="cursor-pointer text-sm font-semibold text-foreground">
          {t("help.toc", "Sur cette page")}
        </summary>
        <div className="mt-3">{list}</div>
      </details>
      <nav className="hidden lg:block" aria-label={t("help.toc", "Sur cette page")}>
        <p className="mb-3 text-xs font-medium tracking-wider text-muted-foreground">
          {t("help.toc", "Sur cette page")}
        </p>
        {list}
      </nav>
    </>
  );
}
